import { Dialog, DialogActions, DialogContent, Drawer } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';

import { useIntl } from 'react-intl';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useCreateAssistantMutation, useUpdateAssistantMutation } from 'services/assistantApi';
import { useNotification } from 'hook/useNotification';
import MDButton from 'components/MDButton';
import AssistantDetails from 'pages/assistants/admin/components/assistantsDetails';

function AssistantDetailsDrawer({ assistant, drawerOpen, setDrawerOpen }) {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();

  const [createAssistant] = useCreateAssistantMutation();
  const [updateAssistant] = useUpdateAssistantMutation();

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [confirmCloseFormDialogOpen, setConfirmCloseFormDialogOpen] = useState(false);

  const toggleDrawer = (open) => () => {
    if (isFormDirty) {
      setConfirmCloseFormDialogOpen(true);
    } else {
      setDrawerOpen(open);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setConfirmCloseFormDialogOpen(false);
  };

  const handleCancelClose = () => {
    setConfirmCloseFormDialogOpen(false);
  };

  const handleFormSubmit = (data) => {
    if (!data.id) {
      handleCreate(data);
    } else {
      handleUpdate(data);
    }
    setDrawerOpen(false);
  };

  const handleFieldChange = (dirty) => {
    setIsFormDirty(dirty);
  };

  return (
    <>
      <Drawer
        variant="temporary"
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          sx: { width: 700, p: 2 }
        }}
      >
        <AssistantDetails
          assistant={assistant}
          onSubmit={handleFormSubmit}
          onFieldChange={handleFieldChange}
        />
      </Drawer>
      <Dialog open={confirmCloseFormDialogOpen} onClose={handleCancelClose}>
        <DialogTitle>{msg({ id: 'dialog.unsaved_changes.title' })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{msg({ id: 'dialog.unsaved_changes.message' })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCancelClose} color="secondary">
            {msg({ id: 'button.cancel' })}
          </MDButton>
          <MDButton onClick={handleCloseDrawer} color="primary">
            {msg({ id: 'button.discard' })}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );

  function prepareAssistantDataForSubmit(data) {
    return {
      ...data,
      users: data?.users.map((user) => user?.id),
      apiKeys: data?.apiKeys.map((apiKey) => apiKey?.id)
    };
  }

  function handleCreate(data) {
    createAssistant(prepareAssistantDataForSubmit(data))
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.assistants.create.success' })
        });
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.assistants.create.error' })
        });
      });
  }

  function handleUpdate(data) {
    updateAssistant({
      assistantId: data.id,
      assistantRequest: prepareAssistantDataForSubmit(data)
    })
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.assistants.update.success' })
        });
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.assistants.update.error' })
        });
      });
  }
}

AssistantDetailsDrawer.propTypes = {
  assistant: PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
    users: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number
      })
    ),
    apiKeys: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number
      })
    ),
    advisors: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number
      })
    )
  }),
  drawerOpen: PropTypes.bool.isRequired,
  setDrawerOpen: PropTypes.func.isRequired
};

export default AssistantDetailsDrawer;
